import { Input as AntInput } from "antd"
import type { TextAreaRef } from "antd/es/input/TextArea"
import React, { type ComponentProps, useRef } from "react"

import { useCapitalizingValue } from "./use-capitalizing-value.ts"

const AntTextArea = AntInput.TextArea

interface TextAreaProps extends ComponentProps<typeof AntTextArea> {
  /** Opts a field out of auto-capitalization. */
  disableAutoCapitalize?: boolean
}

/**
 * Drop-in replacement for antd's `Input.TextArea` that capitalizes only the
 * very first character of the field's content — not every line or sentence.
 * See `useCapitalizingValue` for the mechanics.
 */
export const TextArea = React.forwardRef<TextAreaRef, TextAreaProps>(
  ({ disableAutoCapitalize, value, defaultValue, onChange, ...props }, ref) => {
    const localRef = useRef<TextAreaRef>(null)

    const setRefs = (node: TextAreaRef | null) => {
      localRef.current = node
      if (typeof ref === "function") ref(node)
      else if (ref) (ref as React.MutableRefObject<TextAreaRef | null>).current = node
    }

    const { currentValue, handleChange } = useCapitalizingValue({
      // Text-only wrapper — antd's broader TextArea value type doesn't
      // apply to how this component is ever used.
      value: value as string | undefined,
      defaultValue: defaultValue as string | undefined,
      disableAutoCapitalize,
      maxLength: props.maxLength,
      onChange,
      getElement: () => localRef.current?.resizableTextArea?.textArea
    })

    return (
      <AntTextArea
        ref={setRefs}
        value={currentValue}
        onChange={handleChange}
        {...props}
      />
    )
  }
)

TextArea.displayName = "TextArea"

export default TextArea
